import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const CodeBlock = ({ code, language = 'javascript' }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        position: 'relative',
        backgroundColor: '#1e1e1e',
        color: '#d4d4d4',
        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
        overflow: 'auto'
      }}
    >
      <Box sx={{ p: { xs: 1, sm: 2 }, pr: { xs: 4, sm: 6 } }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          <code>{code}</code>
        </pre>
      </Box>
      <Tooltip title="Copy code">
        <IconButton
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: { xs: 4, sm: 8 },
            right: { xs: 4, sm: 8 },
            color: '#d4d4d4',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
          size="small"
        >
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

const ApiEndpointInfo = ({ open, onClose, schema }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!schema) return null;

  const baseUrl = window.location.origin;
  const apiEndpoint = `${baseUrl}/api/data/${schema.slug}`;

  const jsExample = `// Fetch all published records
fetch('${apiEndpoint}')
  .then(response => response.json())
  .then(data => {
    console.log('Schema:', data.schema);
    console.log('Records:', data.data);
  });

// Fetch with parameters
fetch('${apiEndpoint}?format=array&limit=10&tags=featured')
  .then(response => response.json())
  .then(records => {
    // Records array for direct use
    console.log(records);
  });`;

  const reactExample = `import { useEffect, useState } from 'react';

function ${schema.name.replace(/[^a-zA-Z]/g, '')}Data() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('${apiEndpoint}?format=array')
      .then(response => response.json())
      .then(records => {
        setData(records);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error:', error);
        setLoading(false);
      });
  }, []);

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {data.map((item, index) => (
        <div key={index}>
          {/* Render your data here */}
          ${schema.fields.slice(0, 2).map(field => 
            `<p>{item.${field.name}}</p>`
          ).join('\n          ')}
        </div>
      ))}
    </div>
  );
}`;

  const nextjsExample = `// pages/api/get-${schema.slug}.js or app/api/${schema.slug}/route.js
export async function generateStaticProps() {
  const response = await fetch('${apiEndpoint}?format=array');
  const data = await response.json();

  return {
    props: {
      ${schema.slug}Data: data,
    },
    revalidate: 60, // Revalidate every 60 seconds
  };
}

// In your component
export default function Page({ ${schema.slug}Data }) {
  return (
    <div>
      {${schema.slug}Data.map((item, index) => (
        <div key={index}>
          ${schema.fields.slice(0, 2).map(field => 
            `<h3>{item.${field.name}}</h3>`
          ).join('\n          ')}
        </div>
      ))}
    </div>
  );
}`;

  const curlExample = `# Get all records
curl "${apiEndpoint}"

# Get records as array format
curl "${apiEndpoint}?format=array"

# Get with filters
curl "${apiEndpoint}?format=array&limit=5&tags=featured"

# Response format (JSON):
{
  "schema": {
    "slug": "${schema.slug}",
    "name": "${schema.name}",
    "fields": [${schema.fields.map(f => `"${f.name}"`).join(', ')}]
  },
  "data": [
    {
      "id": "...",
      "data": {
        ${schema.fields.slice(0, 2).map(field => 
          `"${field.name}": "example value"`
        ).join(',\n        ')}
      },
      "createdAt": "2024-01-01T00:00:00Z"
    }
  ]
}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={{ xs: true, sm: false }}
      PaperProps={{
        sx: { 
          height: { xs: '100vh', sm: '80vh' },
          m: { xs: 0, sm: 3 }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              API Documentation
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {schema.name} ({schema.slug})
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Public API Endpoint
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' }, 
            gap: 1, 
            mb: 2 
          }}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 1, 
                flexGrow: 1, 
                fontFamily: 'monospace',
                backgroundColor: '#f5f5f5',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                wordBreak: 'break-all'
              }}
            >
              {apiEndpoint}
            </Paper>
            <Tooltip title="Copy endpoint">
              <IconButton 
                onClick={() => {
                  navigator.clipboard.writeText(apiEndpoint);
                  toast.success('Endpoint copied to clipboard');
                }}
                sx={{ alignSelf: { xs: 'center', sm: 'auto' } }}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="GET" color="success" size="small" />
            <Chip label="Public" color="info" size="small" />
            <Chip label="CORS Enabled" color="primary" size="small" />
            <Chip label="No Auth Required" color="default" size="small" />
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                minWidth: { xs: 100, sm: 'auto' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
          >
            <Tab label="JavaScript" />
            <Tab label="React" />
            <Tab label="Next.js SSG" />
            <Tab label="cURL" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Basic JavaScript Usage
          </Typography>
          <CodeBlock code={jsExample} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            React Component Example
          </Typography>
          <CodeBlock code={reactExample} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Next.js Static Site Generation
          </Typography>
          <CodeBlock code={nextjsExample} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            cURL Examples
          </Typography>
          <CodeBlock code={curlExample} language="bash" />
        </TabPanel>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Query Parameters
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" component="div">
              <strong>format</strong>: Response format
              <br />• <code>json</code> (default) - Full response with schema info
              <br />• <code>array</code> - Simple array of records
              <br /><br />
              <strong>limit</strong>: Maximum records to return (1-100, default: 100)
              <br /><br />
              <strong>page</strong>: Page number for pagination (default: 1)
              <br /><br />
              <strong>tags</strong>: Filter by tags (comma-separated)
              <br />Example: <code>?tags=featured,popular</code>
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Schema Fields
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {schema.fields.map((field) => (
              <Box key={field.name} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{field.name}</strong> ({field.type})
                  {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                  <br />
                  <em>{field.label}</em>
                  {field.description && (
                    <>
                      <br />
                      {field.description}
                    </>
                  )}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiEndpointInfo; 